import { ReactionCounts, REACTIONS } from "@/src/models/post.model";
import { useGetReactions } from "@/src/services/api/posts/posts.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Modal, Pressable, ScrollView, Text, View } from "react-native";

type ReactorModalProps = Readonly<{
	postId: string;
	reactions: ReactionCounts;
	showModal: boolean;
	onClose: () => void;
}>;

const ReactorModal = ({ showModal, onClose, reactions, postId }: ReactorModalProps) => {
	const { themeVars } = useTheme();
	const {t} = useTranslation();

	const [selectedReaction, setSelectedReaction] = useState<string>("");

	useEffect(() => {
		if (showModal) {
			const firstActiveReaction =
				REACTIONS.find(r => reactions[r.id] > 0)?.id || REACTIONS[0].id;

			setSelectedReaction(firstActiveReaction);
		}
	}, [showModal, reactions]);

	const activeReactions = REACTIONS.filter(reaction => reactions[reaction.id] > 0);

	const { data } = useGetReactions(postId);
	const reactionsData = data?.pages.flatMap((page) => page?.getreactions?.items ?? []) ?? [];

	// Filter reactions by selected type
	const filteredReactions = reactionsData.filter(item => item.type === selectedReaction);

	return (
		<Modal visible={showModal} transparent animationType="fade">
			<View style={themeVars as any} className="flex-1">
				<View
					className="flex-1 items-center justify-center px-4"
					style={{ backgroundColor: 'var(--color-shadow-dark)' }}
				>
					<View
						className="w-full max-w-md bg-white rounded-2xl"
						style={{ height: "80%", maxHeight: 600 }}
					>
						{/* Header */}
						<View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100">
							<Text className="text-lg font-semibold text-gray-900">{t("LABEL_REACTIONS")}</Text>
							<Pressable onPress={onClose} className="p-1.5 hover:bg-ash-3 hover:rounded-full">
								<CloseCircle size={20} color={'var(--color-ash-1)'} />
							</Pressable>
						</View>

						{/* Emoji Tabs */}
						{activeReactions.length > 0 ? (
							<>
								<View className="border-b border-gray-100">
									<View className="flex-row px-2">
										{activeReactions.map((reaction) => (
											<Pressable
												key={reaction.id}
												onPress={() => setSelectedReaction(reaction.id)}
												className={`flex-row gap-2 px-4 py-3 items-center hover:rounded-lg ${selectedReaction === reaction.id ? 'border-b-2 border-blue-500 hover:bg-primary-2' : 'hover:bg-ash-3'
													}`}
											>
												<Text className="text-xl">{reaction.emoji}</Text>
												<Text
													className={`text-xs mt-1 ${selectedReaction === reaction.id
															? 'text-blue-500 font-semibold'
															: 'text-gray-500'
														}`}
												>
													{reactions[reaction.id]}
												</Text>
											</Pressable>
										))}
									</View>
								</View>

								<ScrollView className="flex-1">
									<View className="p-5">
										{filteredReactions.length > 0 ? (
											<View className="space-y-3">
												{filteredReactions.map((reaction, index) => (
													<View
														key={reaction.ItemId || index}
														className="flex-row items-center gap-3"
													>
														<Image
															source={{ uri: reaction.reactorProfileImageUrl }}
															className="w-10 h-10 rounded-full"
														/>
														<View className="flex-1">
															<Text className="text-gray-900 font-medium">
																{reaction.reactorName || 'Unknown User'}
															</Text>
														</View>
													</View>
												))}
											</View>
										) : (
											<Text className="text-gray-500 mt-2">{t("LABEL_NO_USERS_YET")}</Text>
										)}
									</View>
								</ScrollView>
							</>
						) : (
							<View className="flex-1 items-center justify-center p-5">
								<Text className="text-gray-500">{t("LABEL_NO_REACTIONS_YET")}</Text>
							</View>
						)}
					</View>
				</View>
			</View>
		</Modal>
	);
};

export default ReactorModal;
